<script lang="ts" setup>
import { UiIcon, UiBtn, UiNewLogoCdk } from '@shared/ui';
import { useMediaQuery } from '@vueuse/core';
import { twMerge } from 'tailwind-merge';
import Menu from './Menu/index.vue';
import MobileMenu from './MobileMenu/index.vue';
import type { HeaderNewProps } from './types';

const { color = 'blue', phoneNumber = '+7 (495) 974-73-74' } = defineProps<HeaderNewProps>();

const menuItems = [
  { id: 'KPlus', label: 'КонсультантПлюс' },
  { id: 'SeminarsAndCourses', label: 'Семинары и курсы' },
  { id: 'About', label: 'О компании' },
  { id: 'Services', label: 'Другие услуги' },
] as const;

const links: {
  path: string;
  label: string;
}[] = [
  { path: '/expertum', label: 'Экспертум' },
];

const phoneNumberHref = computed(() => `tel:${phoneNumber.replace(/[\s()-]/g, '')}`);
const isMobile = useMediaQuery('(max-width: 892px)');
const { y: windowY } = useWindowScroll();

const currentColorSchema = ref(color);
const isOpenMenu = ref(false);
const activeMenu = ref<'KPlus' | 'SeminarsAndCourses' | 'About' | 'Services' | null>(null);

watch(windowY, (y) => {
  currentColorSchema.value = y > 0 ? 'white' : color;
});

const theme = computed<{
  bg: string;
  buttonBg: string;
  menuButtonHover: string;
  menuButtonActive: string;
  text: string;
  phoneText: string;
  burger: string;
  button: string;
  logo: {
    main: string;
    twoLine: string;
    icon: string;
  };
}>(() => {
  const base = currentColorSchema.value;
  return {
    bg: base === 'blue' ? 'bg-blue-950' : 'bg-white',
    buttonBg: base === 'blue' ? 'bg-gray-100/20' : 'bg-gray-500/20',
    menuButtonHover: base === 'blue' ? 'hover:bg-gray-10/20' : 'hover:bg-gray-500/10',
    menuButtonActive: base === 'blue' ? 'active:bg-gray-10/30' : 'active:bg-gray-500/20',
    text: base === 'blue' ? 'text-white' : 'text-gray-900',
    phoneText: base === 'blue' ? 'text-white' : 'text-blue-600',
    burger: base === 'blue' ? 'text-white hover:text-gray-50' : 'text-gray-500 hover:text-gray-700',
    button: base === 'blue'
      ? 'bg-white text-blue-950 hover:bg-gray-50'
      : 'bg-blue-600 text-white hover:bg-blue-700',
    logo: {
      main: base === 'blue' ? 'fill-white' : 'fill-blue-600',
      twoLine: base === 'blue' ? 'fill-white' : 'fill-gray-900',
      icon: base === 'blue' ? 'stroke-white' : 'stroke-gray-500',
    },
  };
});

const classNames = computed(() => ({
  root: twMerge(
    'sticky top-0 z-50 flex flex-col max-[744px]:flex-row items-center',
    'gap-2 pt-3.25 pb-2 px-7.5 max-[444px]:pb-3.25 max-[445px]:px-2.5 max-[744px]:gap-4 transition-colors',
    theme.value.bg,
  ),
  demoButton: twMerge(
    'flex items-center rounded-full py-2 px-4 max-[720px]:hidden',
    'whitespace-nowrap text-sm leading-normal transition-colors',
    theme.value.button,
  ),
  phoneButton: twMerge(
    'whitespace-nowrap flex items-center text-base font-bold transition-colors max-[744px]:!my-auto',
    theme.value.phoneText,
  ),
  menuButton: (isActive: boolean) => twMerge(
    'flex items-center text-sm gap-1 rounded-[4px] py-1 px-2 h-6 transition max-[893px]:hidden',
    theme.value.text,
    theme.value.menuButtonHover,
    theme.value.menuButtonActive,
    isActive && `${theme.value.buttonBg}`,
    'active:duration-75',
  ),
  burgerButton: twMerge(
    'hidden max-[744px]:inline-flex items-center justify-center p-0.5',
    'transition-colors cursor-pointer',
    theme.value.burger,
  ),
  burgerButtonDown: twMerge(
    'hidden max-[893px]:!inline-flex items-center justify-center p-0.5',
    'transition-colors cursor-pointer',
    theme.value.burger,
  ),
}));

const toggleMenu = (menuType: typeof activeMenu.value) => {
  activeMenu.value = activeMenu.value === menuType ? null : menuType;
  isOpenMenu.value = !!activeMenu.value;
};

const toggleMobileMenu = () => {
  isOpenMenu.value = !isOpenMenu.value;
  activeMenu.value = null;
};
</script>

<template>
  <header :class="classNames.root">
    <button
      :class="classNames.burgerButton"
      @click="toggleMobileMenu"
    >
      <UiIcon
        :name="isOpenMenu ? 'close' : 'menu'"
        class="w-4 h-4"
      />
      <span class="sr-only">Открыть меню</span>
    </button>

    <div class="w-full max-w-[1128px] border-b-2 pb-3 border-gray-100/40 max-[744px]:border-0 max-[744px]:pb-0">
      <div class="w-full max-w-layout mx-auto my-0 flex flex-row items-center justify-between">
        <UiNewLogoCdk
          :logo-color="theme.logo.main"
          :first-line-color="theme.logo.main"
          :two-line-color="theme.logo.twoLine"
          :icon-color="theme.logo.icon"
        />

        <div class="flex items-center gap-6">
          <div class="flex flex-row gap-6 items-start max-[744px]:gap-4">
            <a
              data-allow-mismatch
              :href="phoneNumberHref"
              :class="classNames.phoneButton"
            >
              <span class="max-[744px]:hidden">{{ phoneNumber }}</span>
              <UiIcon
                name="phone"
                class="!hidden max-[744px]:!inline-flex !w-6 !h-6"
              />
            </a>
            <UserProfile
              :color="currentColorSchema === 'blue' ? 'default' : 'primary'"
              class="!hidden max-[744px]:!flex"
            />
            <a
              href="https://www.4dk.ru/home/myspace"
              :class="twMerge('flex !underline items-center text-sm max-[744px]:!hidden', theme.phoneText)"
            >
              Информер
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-row min-h-[37px] max-w-[1128px] justify-between w-full max-[744px]:hidden">
      <button
        :class="classNames.burgerButtonDown"
        @click="toggleMobileMenu"
      >
        <UiIcon
          :name="isOpenMenu ? 'close' : 'menu'"
          class="w-4 h-4"
        />
        <span class="sr-only">Открыть меню</span>
      </button>
      <div class="flex flex-row gap-10 max-[1000px]:gap-5 my-auto">
        <template
          v-for="item in menuItems.slice(0, 1)"
          :key="item.id"
        >
          <button
            :class="classNames.menuButton(activeMenu === item.id && isOpenMenu)"
            @click="toggleMenu(item.id)"
          >
            <span class="whitespace-nowrap">{{ item.label }}</span>
            <UiIcon :name="activeMenu === item.id && isOpenMenu ? 'chevron-up' : 'chevron-down'" />
          </button>
        </template>

        <template
          v-for="link in links"
          :key="link.path"
        >
          <UiBtn
            tag="a"
            :to="link.path"
            color="muted"
            :label="link.label"
            :class="twMerge('!rounded-[4px] !border-0 !text-sm !py-1 !px-1 focus:!outline-none focus:!ring-0', classNames.menuButton(false))"
          />
        </template>

        <template
          v-for="item in menuItems.slice(1, 4)"
          :key="item.id"
        >
          <button
            :class="classNames.menuButton(activeMenu === item.id && isOpenMenu)"
            @click="toggleMenu(item.id)"
          >
            <span class="whitespace-nowrap">{{ item.label }}</span>
            <UiIcon :name="activeMenu === item.id && isOpenMenu ? 'chevron-up' : 'chevron-down'" />
          </button>
        </template>
      </div>
      <UserProfile :color="currentColorSchema === 'blue' ? 'default' : 'primary'" />
    </div>

    <Menu
      v-if="!isMobile"
      :is-open="isOpenMenu"
      :active-menu="activeMenu"
      @close="isOpenMenu = false"
    />
    <MobileMenu
      v-else
      :is-open="isOpenMenu"
      @close="isOpenMenu = false"
    />
  </header>
</template>
